/* TabGroup — matches Figma "Segment" container (node 1110:2951).
 * Pill-shaped wrapper with 2px internal padding, 8px radius,
 * --bg-secondary fill. Flex row of tabs with no gap (tabs sit
 * flush against each other). */
.tabGroup {
  display: inline-flex;
  align-items: center;
  padding: var(--spacing-2);
  background: var(--bg-secondary);
  border-radius: var(--radius-8);
}

/* Tab — matches Figma "Segment tab" button (nodes 1108:2050 / 2053).
 * Height 28px, 12px horizontal / 4px vertical padding, 4px gap
 * between icon + label, 6px radius. Default state is transparent
 * (no bg, no shadow), selected paints a raised surface. */
.tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-4);
  height: 28px;
  padding: 4px var(--spacing-12);
  /* --radius-6 (not --radius-8 on the container) — the tab is
   * slightly tighter than its wrapper so the selected surface
   * reads as a nested pill. */
  border-radius: var(--radius-6);
  border: none;
  background: transparent;
  color: var(--text-primary);
  /* Typography matches the Figma spec verbatim: Medium/Micro =
   * 12px / 20px line-height / 500 weight with --text-xs-tracking. */
  font: var(--text-micro-medium);
  letter-spacing: var(--text-xs-tracking);
  cursor: pointer;
  /* Match Button/IconButton interactive primitives — consistent
   * focus ring via box-shadow, scoped transitions. */
  transition:
    background var(--motion-duration-fast),
    box-shadow var(--motion-duration-fast);
}

.tab:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Icon-only — tighter horizontal padding so the tab reads as a
 * 28×28 square (14px glyph + 2×7px padding = 28px wide). Matches
 * the Tab height, giving the segmented control a consistent
 * icon-button shape. The Figma "Segment tab" variant set only
 * ships the labeled form; icon-only here extends the primitive
 * for view-mode toggles (grid / list / calendar / etc.) and
 * follows the padding convention IconButton uses at size="m"
 * (glyph + 4px padding on each side) — here we use 7px to hit
 * the 28px square target given the 14px Icon size="xs". */
.tab.iconOnly {
  padding: 4px 7px;
}

/* Selected — Figma "Shadow/m" drop shadow via --shadow-m (hand-
 * authored in sync-figma-tokens.ts since Figma Effect Styles aren't
 * in the Variables API). The selected surface is --bg-primary in
 * light (pure white on grey) and --bg-tertiary in dark
 * (#404040 on #262626) per the Figma spec. */
.tab.selected {
  background: var(--bg-primary);
  box-shadow: var(--shadow-m);
}

[data-theme="dark"] .tab.selected {
  background: var(--bg-tertiary);
}

/* Hover — only on non-selected, non-disabled tabs. Figma doesn't
 * explicitly spec a hover on the segment tab (the selected/default
 * split is the whole interaction), but a subtle hint makes the
 * affordance obvious before click. Reuse --icon-bg-hover (the
 * purpose-built icon-button hover surface) — it's the same hex as
 * --bg-secondary in tokens-figma, but is the semantic choice for
 * "button hover over a secondary surface." */
.tab:not(.selected):not(:disabled):hover {
  background: var(--icon-bg-hover);
}

/* Focus-visible — 1px outset ring matching Button/IconButton/Link.
 * --text-primary swaps light/dark automatically (neutral-600
 * light, near-white dark). */
.tab:focus-visible {
  outline: none;
  box-shadow: 0 0 0 1px var(--text-primary);
}

/* Selected + focus-visible — layer the accent ring on top of
 * --shadow-m so both signals stay readable. */
.tab.selected:focus-visible {
  box-shadow:
    0 0 0 1px var(--text-primary),
    var(--shadow-m);
}

/* Label sits in its own span so the flex gap applies between the
 * icon and the label regardless of whether the icon is present. */
.label {
  white-space: nowrap;
}
